import {
  type RankQuestionAnswerDefinitionOptionDto,
  RankQuestionDefinitionDto,
  UserQuizQuestionAnswerDto,
} from '@/api/generated';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import DraggableList from '@/components/draggableList/DraggableList.tsx';
import { DomPurifyConfig } from '@/config/DomPurify.config.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import DOMPurify from 'dompurify';
import parseHtml from 'html-react-parser';
import React, { useState } from 'react';
import { Form } from 'react-bootstrap';

interface RankQuestionProps {
  disabled: boolean;
  onAnswerSelected: (questionDefinition: UserQuizQuestionAnswerDto) => void;
  questionDefinition: {
    discriminatorType: 'RANK';
  } & RankQuestionDefinitionDto;
}

const RankQuestionForm: React.FC<RankQuestionProps> = ({
  disabled,
  onAnswerSelected,
  questionDefinition,
}) => {
  const { t } = useTranslationWithPrefix('views.process.quizQuestions');
  const { answerDefinition, question } = questionDefinition;

  const [formData, setFormData] = useState<RankQuestionAnswerDefinitionOptionDto[]>(
    answerDefinition.options,
  );
  // The order is the candidate's work and stays in state; the texts always come from the current
  // question, so they follow a language switch
  const answersByIndex = new Map(
    answerDefinition.options.map(option => [option.index, option.answer]),
  );
  const items = formData.map(item => ({
    ...item,
    answer: answersByIndex.get(item.index) ?? item.answer,
  }));

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onAnswerSelected({
      answers: formData.map(item => ({
        answerDefinitionIndex: item.index,
        answerPositionIndex: item.order,
      })),
      discriminatorType: questionDefinition.discriminatorType,
    });
  };

  return (
    <Form className="flex flex-column gap-l" onSubmit={handleSubmit}>
      <p className="lead fw-bold">{parseHtml(DOMPurify.sanitize(question, DomPurifyConfig))}</p>
      <Form.Group>
        <DraggableList items={items} setItems={setFormData} />
      </Form.Group>
      <ButtonWithLoading disabled={disabled} type="submit">
        {t('buttons.next')}
      </ButtonWithLoading>
    </Form>
  );
};

export default RankQuestionForm;
